import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Plus, Users, Mail, Phone, Shield, Eye, Edit, Loader2, Trash2, MoreVertical, X, Network } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { CareCircleRadar } from '@/components/CareCircleRadar';

interface CareCircleMember {
  id: string;
  name: string;
  email: string;
  relationship: string;
  avatar: string;
  status: 'active' | 'pending';
  contactFrequency?: string;
  accessLevel: string;
  interests?: string[];
}

const relationshipOptions = [
  { label: 'Spouse', value: 'Spouse' },
  { label: 'Partner', value: 'Partner' },
  { label: 'Parent', value: 'Parent' },
  { label: 'Child', value: 'Child' },
  { label: 'Sibling', value: 'Sibling' },
  { label: 'Grandparent', value: 'Grandparent' },
  { label: 'Grandchild', value: 'Grandchild' },
  { label: 'Aunt/Uncle', value: 'Aunt/Uncle' },
  { label: 'Cousin', value: 'Cousin' },
  { label: 'Friend', value: 'Friend' },
  { label: 'Primary Care Doctor', value: 'Primary Care Doctor' },
  { label: 'Specialist', value: 'Specialist' },
  { label: 'Nurse', value: 'Nurse' },
  { label: 'Therapist', value: 'Therapist' },
  { label: 'Caregiver', value: 'Caregiver' },
  { label: 'Legal Guardian', value: 'Legal Guardian' },
  { label: 'Power of Attorney', value: 'Power of Attorney' },
  { label: 'Other', value: 'Other' }
];

const contactFrequencyOptions = [
  { label: 'Hourly', value: 'Hourly' },
  { label: 'Daily', value: 'Daily' },
  { label: 'Weekly', value: 'Weekly' },
  { label: 'Monthly', value: 'Monthly' },
  { label: 'Quarterly', value: 'Quarterly' },
  { label: 'Annually', value: 'Annually' },
  { label: 'Occasionally', value: 'Occasionally' }
];

const accessLevelOptions = [
  { label: 'Viewer', value: 'Viewer' },
  { label: 'Caregiver', value: 'Caregiver' }
];

const interestsOptions = [
  { label: 'Physical', value: 'Physical' },
  { label: 'Mental', value: 'Mental' },
  { label: 'Emotional', value: 'Emotional' },
  { label: 'Spiritual', value: 'Spiritual' },
  { label: 'Social', value: 'Social' },
  { label: 'Financial', value: 'Financial' },
  { label: 'Occupational', value: 'Occupational' }
];

export const CareCircle = () => {
  const [members, setMembers] = useState<CareCircleMember[]>([]);

  const [isAddingMember, setIsAddingMember] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newMember, setNewMember] = useState({
    name: '',
    email: '',
    relationship: '',
    contactFrequency: '',
    accessLevel: 'Viewer',
    interests: [] as string[]
  });

  const [editingMember, setEditingMember] = useState<CareCircleMember | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState<CareCircleMember | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isRadarOpen, setIsRadarOpen] = useState(false);

  // Interest combobox state
  const [interestInput, setInterestInput] = useState('');
  const [showInterestDropdown, setShowInterestDropdown] = useState(false);
  const [interestInputEdit, setInterestInputEdit] = useState('');
  const [showInterestDropdownEdit, setShowInterestDropdownEdit] = useState(false);

  const { toast } = useToast();

  const loadCareCircleMembers = useCallback(async () => {
    try {
      const token = localStorage.getItem('bearer_token');
      if (!token) return;

      const response = await fetch('/api/care_circle_members', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.members) {
          setMembers(data.members);
        }
      }
    } catch (error) {
      console.error('Error loading Care Circle members:', error);
    }
  }, []);

  // Load Care Circle members from API
  useEffect(() => {
    loadCareCircleMembers();
  }, [loadCareCircleMembers]);

  const handleAddMember = async () => {
    if (!newMember.name || !newMember.email || !newMember.relationship) {
      toast({
        title: "Error",
        description: "Please fill in all required fields",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const token = localStorage.getItem('bearer_token');
      if (!token) {
        throw new Error('Authentication required');
      }

      const response = await fetch('/api/invite_care_circle_member', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(newMember)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to invite member');
      }

      const result = await response.json();

      // Add the new member to the local state
      const member: CareCircleMember = {
        id: result.id || Date.now().toString(),
        ...newMember,
        avatar: '👤',
        status: 'pending'
      };

      setMembers([...members, member]);
      setNewMember({ name: '', email: '', relationship: '', contactFrequency: '', accessLevel: 'Viewer', interests: [] });
      setIsAddingMember(false);

      toast({
        title: "Invitation Sent",
        description: `${newMember.name} has been invited to join your Care Circle`,
      });

    } catch (error) {
      console.error('Error inviting member:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to invite member",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditMember = (member: CareCircleMember) => {
    setEditingMember(member);
    setIsEditDialogOpen(true);
  };

  const handleUpdateMember = async () => {
    if (!editingMember) return;

    setIsUpdating(true);

    try {
      const token = localStorage.getItem('bearer_token');
      if (!token) {
        throw new Error('Authentication required');
      }

      const response = await fetch(`/api/care_circle_member/${editingMember.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          accessLevel: editingMember.accessLevel,
          contactFrequency: editingMember.contactFrequency,
          interests: editingMember.interests
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update member');
      }

      // Update local state
      setMembers(members.map(m =>
        m.id === editingMember.id ? editingMember : m
      ));

      setIsEditDialogOpen(false);
      setEditingMember(null);

      toast({
        title: "Success",
        description: "Member updated successfully",
      });

    } catch (error) {
      console.error('Error updating member:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to update member",
        variant: "destructive"
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteMember = (member: CareCircleMember) => {
    setMemberToDelete(member);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!memberToDelete) return;

    setIsDeleting(true);

    try {
      const token = localStorage.getItem('bearer_token');
      if (!token) {
        throw new Error('Authentication required');
      }

      const response = await fetch(`/api/care_circle_member/${memberToDelete.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete member');
      }

      // Remove from local state
      setMembers(members.filter(m => m.id !== memberToDelete.id));

      setIsDeleteDialogOpen(false);
      setMemberToDelete(null);

      toast({
        title: "Success",
        description: `${memberToDelete.name} has been removed from your Care Circle`,
      });

    } catch (error) {
      console.error('Error deleting member:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to delete member",
        variant: "destructive"
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const getRoleIcon = (accessLevel: string) => {
    switch (accessLevel) {
      case 'Caregiver': return <Edit className="h-4 w-4 text-orange-500" />;
      case 'Viewer': return <Eye className="h-4 w-4 text-blue-500" />;
      default: return <Eye className="h-4 w-4 text-blue-500" />;
    }
  };

  const getRoleColor = (accessLevel: string) => {
    switch (accessLevel) {
      case 'Caregiver': return 'bg-orange-100 text-orange-700';
      case 'Viewer': return 'bg-blue-100 text-blue-700';
      default: return 'bg-blue-100 text-blue-700';
    }
  };

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold">Care Circle</h2>
          <p className="text-sm md:text-base text-muted-foreground">
            Manage who can access and help with your health information
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 w-full md:w-auto">
          <Button
            variant="outline"
            className="flex items-center justify-center space-x-2 w-full sm:w-auto"
            onClick={() => setIsRadarOpen(true)}
          >
            <Network className="h-4 w-4" />
            <span>View My Circle</span>
          </Button>
          <Dialog open={isAddingMember} onOpenChange={setIsAddingMember}>
            <DialogTrigger asChild>
              <Button className="flex items-center justify-center space-x-2 w-full sm:w-auto">
                <Plus className="h-4 w-4" />
                <span>Add Member</span>
              </Button>
            </DialogTrigger>
            <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Care Circle Member</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium">Name *</label>
                <Input
                  value={newMember.name}
                  onChange={(e) => setNewMember({ ...newMember, name: e.target.value })}
                  placeholder="Enter full name"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Email *</label>
                <Input
                  type="email"
                  value={newMember.email}
                  onChange={(e) => setNewMember({ ...newMember, email: e.target.value })}
                  placeholder="Enter email address"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Relationship *</label>
                <Select value={newMember.relationship} onValueChange={(value) => setNewMember({ ...newMember, relationship: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select relationship" />
                  </SelectTrigger>
                  <SelectContent>
                    {relationshipOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">Access Level</label>
                <Select value={newMember.accessLevel} onValueChange={(value) => setNewMember({ ...newMember, accessLevel: value })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {accessLevelOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">Contact Frequency</label>
                <Select value={newMember.contactFrequency} onValueChange={(value) => setNewMember({ ...newMember, contactFrequency: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select frequency" />
                  </SelectTrigger>
                  <SelectContent>
                    {contactFrequencyOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">Interests</label>
                <div className="space-y-2">
                  <div className="relative">
                    <Input
                      value={interestInput}
                      onChange={(e) => {
                        setInterestInput(e.target.value);
                        setShowInterestDropdown(true);
                      }}
                      onFocus={() => setShowInterestDropdown(true)}
                      onBlur={() => {
                        // Delay to allow click on dropdown items
                        setTimeout(() => setShowInterestDropdown(false), 200);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          const value = interestInput.trim();
                          if (value && !newMember.interests.includes(value)) {
                            setNewMember({
                              ...newMember,
                              interests: [...newMember.interests, value]
                            });
                            setInterestInput('');
                            // Keep dropdown open for easy multi-add
                          }
                        }
                      }}
                      placeholder="Type or select interest"
                    />
                    {showInterestDropdown && (
                      <div className="absolute z-10 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-auto">
                        {interestsOptions
                          .filter(option =>
                            !newMember.interests.includes(option.value) &&
                            option.label.toLowerCase().includes(interestInput.toLowerCase())
                          )
                          .map((option) => (
                            <div
                              key={option.value}
                              className="px-3 py-2 cursor-pointer hover:bg-gray-100"
                              onMouseDown={(e) => {
                                e.preventDefault();
                                setNewMember({
                                  ...newMember,
                                  interests: [...newMember.interests, option.value]
                                });
                                setInterestInput('');
                                // Keep dropdown open for easy multi-select
                              }}
                            >
                              {option.label}
                            </div>
                          ))}
                        {interestInput.trim() &&
                         !interestsOptions.some(opt =>
                           opt.label.toLowerCase() === interestInput.toLowerCase()
                         ) && (
                          <div className="px-3 py-2 text-sm text-muted-foreground border-t">
                            Press Enter to add "{interestInput}" as custom interest
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                {newMember.interests.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {newMember.interests.map((dimension) => (
                      <Badge
                        key={dimension}
                        variant="outline"
                        className="flex items-center gap-1 pr-1"
                      >
                        {dimension}
                        <button
                          type="button"
                          onClick={() => {
                            setNewMember({
                              ...newMember,
                              interests: newMember.interests.filter(d => d !== dimension)
                            });
                          }}
                          className="ml-1 rounded-full hover:bg-muted"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex space-x-2">
                <Button 
                  onClick={handleAddMember} 
                  className="flex-1"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Sending Invitation...
                    </>
                  ) : (
                    'Send Invitation'
                  )}
                </Button>
                <Button variant="outline" onClick={() => setIsAddingMember(false)}>
                  Cancel
                </Button>
              </div>
            </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Permissions Overview */}
      <Card className="p-4 md:p-6">
        <h3 className="text-lg font-semibold mb-4">Access Levels</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-3 md:p-4 rounded-lg border">
            <div className="flex items-center space-x-2 mb-2">
              <Shield className="h-5 w-5 text-primary" />
              <span className="font-medium">Owner</span>
            </div>
            <p className="text-sm text-muted-foreground">
              Full access to all health data, settings, and can manage care circle
            </p>
          </div>
          <div className="p-3 md:p-4 rounded-lg border">
            <div className="flex items-center space-x-2 mb-2">
              <Edit className="h-5 w-5 text-orange-500" />
              <span className="font-medium">Caregiver</span>
            </div>
            <p className="text-sm text-muted-foreground">
              Can view health data, share documents, and receive alerts
            </p>
          </div>
          <div className="p-3 md:p-4 rounded-lg border">
            <div className="flex items-center space-x-2 mb-2">
              <Eye className="h-5 w-5 text-blue-500" />
              <span className="font-medium">Viewer</span>
            </div>
            <p className="text-sm text-muted-foreground">
              Read-only access to basic health information
            </p>
          </div>
        </div>
      </Card>

      {/* Members List */}
      <Card className="p-4 md:p-6">
        <h3 className="text-lg font-semibold mb-4">Members ({members.length})</h3>
        <div className="space-y-3">
          {members.map((member) => (
            <div key={member.id} className="p-3 md:p-4 rounded-lg border relative">
              {/* Mobile Actions - Top Right */}
              <div className="absolute top-3 right-3 flex items-center gap-1 sm:hidden">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => window.location.href = `mailto:${member.email}`}
                  title={`Send email to ${member.email}`}
                  className="h-8 w-8 p-0"
                >
                  <Mail className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0"
                >
                  <Phone className="h-4 w-4" />
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0"
                    >
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => handleEditMember(member)}>
                      <Edit className="h-4 w-4 mr-2" />
                      Edit Member
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => handleDeleteMember(member)}
                      className="text-destructive focus:text-destructive"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Remove
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              <div className="flex items-start sm:justify-between gap-3">
                {/* Left Section - Member Info */}
                <div className="flex items-start space-x-3 flex-1 min-w-0 pr-20 sm:pr-0">
                  <div className="h-10 w-10 rounded-full bg-primary/20 flex items-center justify-center text-lg flex-shrink-0">
                    {member.avatar}
                  </div>
                  <div className="flex-1 min-w-0 space-y-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="font-medium">{member.name}</h4>
                      {member.status === 'pending' && (
                        <Badge variant="outline" className="text-xs">
                          Pending
                        </Badge>
                      )}
                      <Badge className={`flex items-center gap-1 ${getRoleColor(member.accessLevel)}`}>
                        {getRoleIcon(member.accessLevel)}
                        <span className="text-xs">{member.accessLevel}</span>
                      </Badge>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground leading-tight">{member.relationship}</p>
                      <p className="text-xs text-muted-foreground truncate leading-tight">{member.email}</p>
                      {member.contactFrequency && (
                        <p className="text-xs text-muted-foreground leading-tight">
                          Contact: {member.contactFrequency}
                        </p>
                      )}
                      {member.interests && member.interests.length > 0 && (
                        <div className="flex flex-wrap gap-1 pt-2">
                          {member.interests.map((dimension) => (
                            <Badge key={dimension} variant="outline" className="text-xs py-0">
                              {dimension}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Desktop Actions - Right Side */}
                <div className="hidden sm:flex items-center gap-1 flex-shrink-0">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => window.location.href = `mailto:${member.email}`}
                    title={`Send email to ${member.email}`}
                  >
                    <Mail className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="sm">
                    <Phone className="h-4 w-4" />
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => handleEditMember(member)}>
                        <Edit className="h-4 w-4 mr-2" />
                        Edit Member
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => handleDeleteMember(member)}
                        className="text-destructive focus:text-destructive"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete Member
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Edit Care Circle Member Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Care Circle Member</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <p className="text-sm text-muted-foreground mb-2">
                Editing: <span className="font-medium">{editingMember?.name}</span>
              </p>
            </div>
            <div>
              <label className="text-sm font-medium">Access Level</label>
              <Select
                value={editingMember?.accessLevel || 'Viewer'}
                onValueChange={(value) => setEditingMember(editingMember ? { ...editingMember, accessLevel: value } : null)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {accessLevelOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">Contact Frequency</label>
              <Select
                value={editingMember?.contactFrequency || ''}
                onValueChange={(value) => setEditingMember(editingMember ? { ...editingMember, contactFrequency: value } : null)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select frequency" />
                </SelectTrigger>
                <SelectContent>
                  {contactFrequencyOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">Interests</label>
              <div className="space-y-2">
                <div className="relative">
                  <Input
                    value={interestInputEdit}
                    onChange={(e) => {
                      setInterestInputEdit(e.target.value);
                      setShowInterestDropdownEdit(true);
                    }}
                    onFocus={() => setShowInterestDropdownEdit(true)}
                    onBlur={() => {
                      // Delay to allow click on dropdown items
                      setTimeout(() => setShowInterestDropdownEdit(false), 200);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        const value = interestInputEdit.trim();
                        if (value && editingMember && !editingMember.interests?.includes(value)) {
                          setEditingMember({
                            ...editingMember,
                            interests: [...(editingMember.interests || []), value]
                          });
                          setInterestInputEdit('');
                          // Keep dropdown open for easy multi-add
                        }
                      }
                    }}
                    placeholder="Type or select interest"
                  />
                  {showInterestDropdownEdit && (
                    <div className="absolute z-10 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-auto">
                      {interestsOptions
                        .filter(option =>
                          !editingMember?.interests?.includes(option.value) &&
                          option.label.toLowerCase().includes(interestInputEdit.toLowerCase())
                        )
                        .map((option) => (
                          <div
                            key={option.value}
                            className="px-3 py-2 cursor-pointer hover:bg-gray-100"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              if (editingMember) {
                                setEditingMember({
                                  ...editingMember,
                                  interests: [...(editingMember.interests || []), option.value]
                                });
                                setInterestInputEdit('');
                                // Keep dropdown open for easy multi-select
                              }
                            }}
                          >
                            {option.label}
                          </div>
                        ))}
                      {interestInputEdit.trim() &&
                       !interestsOptions.some(opt =>
                         opt.label.toLowerCase() === interestInputEdit.toLowerCase()
                       ) && (
                        <div className="px-3 py-2 text-sm text-muted-foreground border-t">
                          Press Enter to add "{interestInputEdit}" as custom interest
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
              {editingMember && editingMember.interests && editingMember.interests.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {editingMember.interests.map((dimension) => (
                    <Badge
                      key={dimension}
                      variant="outline"
                      className="flex items-center gap-1 pr-1"
                    >
                      {dimension}
                      <button
                        type="button"
                        onClick={() => {
                          setEditingMember({
                            ...editingMember,
                            interests: editingMember.interests?.filter(d => d !== dimension) || []
                          });
                        }}
                        className="ml-1 rounded-full hover:bg-muted"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>
            <div className="flex space-x-2">
              <Button
                onClick={handleUpdateMember}
                className="flex-1"
                disabled={isUpdating}
              >
                {isUpdating ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Updating...
                  </>
                ) : (
                  'Update'
                )}
              </Button>
              <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Care Circle Member</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <p className="text-sm">
                Are you sure you want to remove <span className="font-medium">{memberToDelete?.name}</span> from your Care Circle?
              </p>
              <p className="text-sm text-muted-foreground mt-2">
                They will no longer have access to your health information. This action cannot be undone.
              </p>
            </div>
            <div className="flex space-x-2">
              <Button
                variant="destructive"
                onClick={confirmDelete}
                className="flex-1"
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  'Delete'
                )}
              </Button>
              <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* My Circle Modal */}
      <Dialog open={isRadarOpen} onOpenChange={setIsRadarOpen}>
        <DialogContent className="max-w-[95vw] md:max-w-4xl max-h-[90vh] overflow-auto p-3 md:p-6">
          <DialogHeader>
            <DialogTitle className="text-base md:text-lg">My Care Circle</DialogTitle>
          </DialogHeader>
          <CareCircleRadar members={members} />
        </DialogContent>
      </Dialog>
    </div>
  );
};
